import { ColumnDef } from '@tanstack/react-table';
import { ArrowUpDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { type PriceSheetItem } from '@/app/services/pricingService';
import { PriceInput } from './PriceInput';

interface ColumnProps {
  modifiedItems: Map<number, number>;
  onPriceChange: (itemId: number, price: number) => void;
  activeCell?: { row: number; col: string } | null;
  onCellFocus?: (rowIndex: number, colId: string) => void;
  onKeyDown?: (e: React.KeyboardEvent, rowIndex: number, colId: string) => void;
  cellRefs?: React.MutableRefObject<Map<string, HTMLElement>>;
}

export const getColumns = ({ 
  modifiedItems, 
  onPriceChange,
  activeCell,
  onCellFocus,
  onKeyDown,
  cellRefs
}: ColumnProps): ColumnDef<PriceSheetItem>[] => [
  {
    id: 'select',
    header: ({ table }) => (
      <Checkbox
        checked={table.getIsAllPageRowsSelected()}
        onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
        aria-label="Select all"
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        checked={row.getIsSelected()}
        onCheckedChange={(value) => row.toggleSelected(!!value)}
        aria-label="Select row"
      />
    ),
    enableSorting: false,
    enableHiding: false,
  },
  {
    accessorKey: 'item_code',
    header: ({ column }) => (
      <Button
        variant="ghost"
        size="sm"
        onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
        className="h-auto px-2 py-1.5"
      >
        <span className="hidden sm:inline">Item </span>Code
        <ArrowUpDown className="ml-1 sm:ml-2 h-3 w-3 sm:h-4 sm:w-4" />
      </Button>
    ),
  },
  {
    accessorKey: 'item_name',
    header: ({ column }) => (
      <Button
        variant="ghost"
        size="sm"
        onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
        className="h-auto px-2 py-1.5"
      >
        <span className="hidden sm:inline">Item </span>Name
        <ArrowUpDown className="ml-1 sm:ml-2 h-3 w-3 sm:h-4 sm:w-4" />
      </Button>
    ),
  },
  {
    accessorKey: 'price',
    header: ({ column }) => (
      <Button
        variant="ghost"
        size="sm"
        onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
        className="h-auto px-2 py-1.5"
      >
        <span className="hidden sm:inline">Current </span>Price
        <ArrowUpDown className="ml-1 sm:ml-2 h-3 w-3 sm:h-4 sm:w-4" />
      </Button>
    ),
    cell: ({ row }) => {
      const price = parseFloat(row.getValue('price')) || 0;
      return <span>₹{price.toFixed(2)}</span>;
    },
  },
  {
    id: 'new_price',
    header: 'New Price',
    cell: () => null, // Handled directly in table body for better control
  },
  {
    id: 'change',
    header: 'Change',
    cell: ({ row }) => {
      const itemId = row.original.item_id;
      const currentPrice = parseFloat(row.original.price) || 0;
      const modifiedPrice = modifiedItems.get(itemId);

      if (modifiedPrice === undefined || modifiedPrice === currentPrice) {
        return <span className="text-muted-foreground">-</span>;
      }

      const change = modifiedPrice - currentPrice;
      const percentChange = currentPrice > 0 ? (change / currentPrice) * 100 : 0;

      return (
        <div className="text-sm">
          <span className={change > 0 ? 'text-success' : 'text-destructive'}>
            {change > 0 ? '+' : ''}₹{change.toFixed(2)}
          </span>
          <span className="text-muted-foreground ml-1">
            ({percentChange > 0 ? '+' : ''}{percentChange.toFixed(1)}%)
          </span>
        </div>
      );
    },
  },
];